import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { submitFeedback } from '../api/endpoints'

interface FeedbackModalProps {
  open:    boolean
  onClose: () => void
}

type Category = 'bug' | 'feature' | 'general'

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'bug',     label: 'Bug' },
  { value: 'feature', label: 'Feature' },
]

export default function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { user } = useAuth()

  const [name,     setName]     = useState('')
  const [email,    setEmail]    = useState('')
  const [message,  setMessage]  = useState('')
  const [category, setCategory] = useState<Category>('general')
  const [status,   setStatus]   = useState<'idle' | 'sending' | 'success' | 'error' | 'validation'>('idle')

  // Pre-fill email when user is logged in
  useEffect(() => {
    if (user?.email) setEmail(user.email)
  }, [user?.email])

  // Escape key closes modal
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Auto-close 1.5s after success
  useEffect(() => {
    if (status !== 'success') return
    const t = setTimeout(onClose, 1500)
    return () => clearTimeout(t)
  }, [status, onClose])

  if (!open) return null

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const emailValid  = EMAIL_RE.test(email.trim())
  const messageValid = message.trim().length >= 10
  const canSubmit = name.trim().length > 0 && email.trim().length > 0 && emailValid && message.trim().length > 0 && status !== 'sending'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    if (!emailValid) { setStatus('error'); return }
    if (!messageValid) { setStatus('validation'); return }
    setStatus('sending')
    try {
      await submitFeedback({ name, email, message, category })
      setName('')
      setMessage('')
      setCategory('general')
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-950/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-neutral-700/60">
          <h2 className="text-sm font-semibold text-neutral-100">Share Feedback</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-5 py-4">

          {/* Category pill toggle */}
          <div className="flex gap-2">
            {CATEGORY_OPTIONS.map(c => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCategory(c.value)}
                className={[
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  category === c.value
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-neutral-800 text-neutral-400 border-neutral-700 hover:border-neutral-500 hover:text-neutral-200',
                ].join(' ')}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-400" htmlFor="fb-name">Name</label>
            <input
              id="fb-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              placeholder="Your name"
              className="w-full rounded-md bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-400" htmlFor="fb-email">Email</label>
            <input
              id="fb-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              maxLength={254}
              placeholder="you@example.com"
              className="w-full rounded-md bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Message */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-400" htmlFor="fb-message">Message</label>
            <textarea
              id="fb-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Tell us what you think..."
              className="w-full rounded-md bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-5 w-full rounded-md bg-primary-600 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'sending' ? 'Sending…' : 'Send Feedback'}
          </button>

          {/* Status row */}
          {status === 'sending' && (
            <p className="text-xs text-neutral-400 mt-2 text-center flex items-center justify-center gap-2">
              <span className="h-3 w-3 animate-spin rounded-full border border-neutral-600 border-t-primary-400" aria-hidden="true" />
              Sending…
            </p>
          )}
          {status === 'success' && (
            <p className="text-xs text-green-400 mt-2 text-center">Feedback sent — thank you!</p>
          )}
          {status === 'error' && (
            <p className="text-xs text-red-400 mt-2 text-center">Failed to send. Please try again.</p>
          )}
          {status === 'validation' && (
            <p className="text-xs text-red-400 mt-2 text-center">
              {!emailValid ? 'Please enter a valid email address.' : 'Message must be at least 10 characters.'}
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
